import { z } from 'zod';

/**
 * The account-owner mails that ride the `email.lifecycle` lane (plan 101 N6) — ONE
 * spelling, shared by the queue payload (`@developerz/queue`) and the worker's send
 * plane (`apps/worker/src/notifications/lifecycle-email.ts`), which is why it lives
 * here and not in either of them. `key_added` / `key_invalid` / `key_restored` are
 * sent inline by their own lanes, so they are not members (deep sweep 03-12).
 *
 * `grant_expiring` (plan 102 slice 05): the grantee's one notice before a capacity
 * grant with an `expires_at` stops counting, enqueued by `cron.grant_expiry`
 * (`apps/worker/src/cron/grant-expiry.ts`).
 */
export const LifecycleEmailTemplate = {
  WelcomeInstalled: 'welcome_installed',
  PolicyInvalid: 'policy_invalid',
  GrantExpiring: 'grant_expiring',
} as const;
export const LifecycleEmailTemplateSchema = z.enum(LifecycleEmailTemplate);
export type LifecycleEmailTemplate = z.infer<typeof LifecycleEmailTemplateSchema>;

/** The `grant_expiring` ledger keys' common prefix — what the "already mailed" read matches. */
export const GRANT_EXPIRING_KEY_PREFIX = `${LifecycleEmailTemplate.GrantExpiring}:`;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * The `grant_expiring` idempotency key: ONE notice per GRANT, whatever the tick, so
 * the `lifecycle_email_sends` ledger is the whole send-once gate (no migration,
 * plan 102 slice 05 decision 5). The lane suffixes it per address
 * (`lifecycleEmailAddressKey`), and `listGrantsExpiringUnmailed` (`@developerz/db`)
 * finds those rows by `starts_with(key, <this>)`. That prefix match is exact only
 * because a grant id is a fixed-width uuid (no grant's key is a prefix of
 * another's), so any other shape is refused here rather than matched wrong there.
 */
export function grantExpiringEmailKey(grantId: string): string {
  if (!UUID_RE.test(grantId)) {
    throw new Error(`grantExpiringEmailKey: grant id must be a lowercase uuid, got ${grantId}`);
  }
  return `${GRANT_EXPIRING_KEY_PREFIX}${grantId}`;
}
